import { Button, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import styled from 'styled-components';
import { useGuideStore } from '../../../stores/guideStore';
import { useNavigation } from '../../../hooks/useNavigation';
import { ButtonWidget as TButtonWidget, WidgetAlignment } from '../../../types';
import { useInspectorStore } from '../../../stores/inspectorStore';
import { CSS_PROPERTIES } from '../../guides/Inspector/constants';
import { useCustomCSSEditor } from '../../../hooks/useCustomCSSEditor';
import { DEFAULT_BUTTON_WIDGET_GAP } from '../helpers';
import { useActiveThemeset } from '../../../queries/themesets';
import { getTypographyWeight } from '../../shared/styled-components/constants';
import { addQuotes, toPx } from '../../guides/Inspector/helpers';
import { useWidgetStyleProperty } from '../../guides/Inspector/helpers';


const ButtonWidget = ({
  widget,
  onEditModeClick,
}: {
  widget: TButtonWidget;
  onEditModeClick: (e: React.MouseEvent) => void;
}) => {
  const { widgetSettings } = useInspectorStore();
  const isActiveWidget = widgetSettings?.widgetId === widget?.id;
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data: themeset } = useActiveThemeset();
  const { getValueFromStyle } = useCustomCSSEditor(widget.style.stylesheet);

  const { value: defaultTextColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.default.textColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );
  const { value: defaultBackgroundColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.default.backgroundColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );
  const { value: defaultBorderColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.default.borderColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );

  const { value: fontFamily } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.fontFamily',
    CSS_PROPERTIES.FONT_FAMILY,
  );
  const { value: fontSize } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.fontSize',
    CSS_PROPERTIES.FONT_SIZE,
  );
  const { value: fontStyle } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.fontStyle',
    CSS_PROPERTIES.FONT_WEIGHT,
  );
  const { value: horizontalPadding } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.padding.horizontalPadding',
    CSS_PROPERTIES.PADDING_LEFT,
  );
  const { value: verticalPadding } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.padding.verticalPadding',
    CSS_PROPERTIES.PADDING_TOP,
  );
  const { value: borderRadius } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );

  const { value: hoverTextColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.hover.textColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );
  const { value: hoverBackgroundColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.hover.backgroundColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );
  const { value: hoverBorderColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.button.colors.hover.borderColor.hexCode',
    CSS_PROPERTIES.COLOR,
  );

  if (!themeset) return null;

  return (
    <StyledButtonWidgetContainer
      $alignment={getValueFromStyle(CSS_PROPERTIES.JUSTIFY_CONTENT)}
      $gap={
        getValueFromStyle(CSS_PROPERTIES.GAP) ??
        `${DEFAULT_BUTTON_WIDGET_GAP}px`
      }
      data-testid="buttonWidgetContainer"
    >
      {widget.buttons.map((button, index) => (
        <ButtonInButtonWidget
          index={index}
          key={button.id}
          title={button.name.text}
          link={button.link.url}
          style={button.style?.stylesheet}
          openNewTab={button.link.openNewTab}
          onEditModeClick={onEditModeClick}
          isActive={
            isEditMode &&
            isActiveWidget &&
            index === widgetSettings?.widgetIndex
          }
          $defaultTextColor={defaultTextColor}
          $defaultBackgroundColor={defaultBackgroundColor}
          $defaultBorderColor={defaultBorderColor}
          $hoverTextColor={hoverTextColor}
          $hoverBackgroundColor={hoverBackgroundColor}
          $hoverBorderColor={hoverBorderColor}
          $borderRadius={borderRadius}
          $fontFamily={fontFamily}
          $fontSize={fontSize}
          $fontStyle={fontStyle}
          $horizontalPadding={horizontalPadding}
          $verticalPadding={verticalPadding}
        />
      ))}
    </StyledButtonWidgetContainer>
  );
};

export const ButtonInButtonWidget = ({
  index,
  isActive,
  title,
  link,
  style,
  openNewTab,
  onEditModeClick,
  $defaultTextColor,
  $defaultBackgroundColor,
  $defaultBorderColor,
  $hoverTextColor,
  $hoverBackgroundColor,
  $hoverBorderColor,
  $borderRadius,
  $fontFamily,
  $fontSize,
  $fontStyle,
  $horizontalPadding,
  $verticalPadding,
}) => {
  const { translate } = useTranslate();
  const { navigate } = useNavigation();
  const isEditMode = useGuideStore(s => s.isEditMode);
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const handleClick = (e: React.MouseEvent) => {
    if (isEditMode) {
      if (widgetSettings?.widgetIndex !== index) onEditModeClick(e);
      setWidgetSettings({ widgetIndex: index });
    } else {
      navigate(link, openNewTab);
    }
  };

  // Generate CSS string from props
  const generateButtonStyles = () => {
    let cssString = '';

    // Base styles
    if ($fontFamily) cssString += `font-family: ${addQuotes($fontFamily)}; `;
    if ($borderRadius) cssString += `border-radius: ${toPx($borderRadius)}; `;
    if ($fontSize) cssString += `font-size: ${toPx($fontSize)}; `;

    // Colors
    if ($defaultTextColor) cssString += `color: ${$defaultTextColor}; `;
    if ($defaultBackgroundColor)
      cssString += `background-color: ${$defaultBackgroundColor}; `;
    if ($defaultBorderColor)
      cssString += `border: 1px solid ${$defaultBorderColor}; `;

    // Padding
    if ($verticalPadding) {
      cssString += `padding-top: ${$verticalPadding}px; `;
      cssString += `padding-bottom: ${$verticalPadding}px; `;
    }
    if ($horizontalPadding) {
      cssString += `padding-left: ${$horizontalPadding}px; `;
      cssString += `padding-right: ${$horizontalPadding}px; `;
    }

    // Hover styles as a CSS block
    let hoverStyles = '';
    if ($hoverTextColor) hoverStyles += `color: ${$hoverTextColor}; `;
    if ($hoverBackgroundColor)
      hoverStyles += `background-color: ${$hoverBackgroundColor}; `;
    if ($hoverBorderColor)
      hoverStyles += `border: 1px solid ${$hoverBorderColor}; `;

    if (hoverStyles) {
      cssString += `&:hover { ${hoverStyles} } `;
    }

    // Add custom CSS from the style prop
    if (style) cssString += style;

    return cssString;
  };

  return (
    <StyledButtonContainer $isActive={isActive}>
      <StyledButton
        data-testid="buttonWidget"
        $styles={generateButtonStyles()}
        $fontStyle={$fontStyle}
        $fontFamily={$fontFamily}
        onClick={handleClick}
        variant="primary"
      >
        {title || translate('BUTTON_WIDGET.PLACEHOLDER')}
      </StyledButton>
    </StyledButtonContainer>
  );
};

const StyledButtonContainer = styled.div<{ $isActive: boolean }>`
  position: relative;
  outline: ${({ $isActive }) =>
    $isActive ? `3px solid ${token.brandPrimary500}` : ''};
  padding: ${({ $isActive }) => ($isActive ? `2px` : `0px`)};
`;

const StyledButton = styled(Button)<{
  $styles: string;
  $fontStyle?: string;
  $fontFamily?: string;
}>`
  && {
    ${props =>
      props.$fontStyle &&
      props.$fontFamily &&
      `font-weight: ${getTypographyWeight(props.theme, props.$fontFamily, props.$fontStyle)};`}
    ${props => props.$styles}
  }
`;

const StyledButtonWidgetContainer = styled.div<{
  $gap: string;
  $alignment: WidgetAlignment;
}>`
  gap: ${props => `${props.$gap}` || `${DEFAULT_BUTTON_WIDGET_GAP}px`};
  display: flex;
  justify-content: ${props => props.$alignment || 'center'};
  flex-wrap: wrap;
`;
export default ButtonWidget;
